import React, { useState, useEffect } from 'react';
import { CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import axios from 'axios';

/**
 * VersionChip — small, unobtrusive deploy-health indicator.
 *
 * Surfaces:
 *   • Frontend bundle hash (extracted from the running <script src="main.HASH.js">
 *     tag — no env-var plumbing required, works automatically with every CRA build)
 *   • Backend health (status, db latency)
 *
 * Refresh strategy: pings /api/health once on mount, then every 5 minutes.
 * Failure is silent (renders a yellow chip) — never breaks the UI.
 *
 * Mount: bottom of admin sidebar so it's always visible without being intrusive.
 */
export default function VersionChip() {
  const [health, setHealth] = useState({ status: 'loading' });
  const [expanded, setExpanded] = useState(false);

  // Extract the frontend bundle hash from the actual loaded <script> tag.
  // CRA emits "main.<8-hex>.js" — that hash changes with every code change,
  // so this is the most reliable indicator of "what's running in this browser".
  const frontendHash = (() => {
    try {
      const scripts = Array.from(document.querySelectorAll('script[src]'));
      const mainScript = scripts.find((s) => /\/main\.[a-f0-9]{6,}\.js$/.test(s.src));
      if (mainScript) {
        const m = mainScript.src.match(/main\.([a-f0-9]{6,})\.js/);
        return m ? m[1].slice(0, 8) : 'dev';
      }
      return 'dev';
    } catch {
      return 'dev';
    }
  })();

  useEffect(() => {
    let mounted = true;
    const fetchHealth = async () => {
      try {
        const res = await axios.get('/api/health', { timeout: 5000 });
        if (mounted) setHealth({ ...res.data, fetched_at: Date.now() });
      } catch (e) {
        if (mounted) setHealth({ status: 'unreachable', error: e.message });
      }
    };
    fetchHealth();
    const interval = setInterval(fetchHealth, 5 * 60 * 1000); // every 5 min
    return () => { mounted = false; clearInterval(interval); };
  }, []);

  const ok = health.status === 'healthy';
  const Icon = health.status === 'loading' ? Loader2 : ok ? CheckCircle2 : AlertCircle;
  const dotColor = health.status === 'loading'
    ? 'text-neutral-400'
    : ok
    ? 'text-emerald-500'
    : 'text-amber-500';

  const backendHash = health?.build?.git_sha && health.build.git_sha !== 'unknown'
    ? health.build.git_sha.slice(0, 8)
    : null;

  return (
    <div
      className="px-3 py-2 border-t border-neutral-200/60 dark:border-white/10 text-xs"
      data-testid="version-chip"
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center gap-2 text-neutral-500 hover:text-neutral-700 dark:hover:text-white transition-colors"
        aria-label="Show deploy version details"
        aria-expanded={expanded}
      >
        <Icon
          className={`w-3.5 h-3.5 ${dotColor} ${health.status === 'loading' ? 'animate-spin' : ''}`}
          aria-hidden="true"
        />
        <span className="font-mono">v.{frontendHash}</span>
        {health?.db?.latency_ms != null && (
          <span className="ml-auto text-[10px] text-neutral-400">
            {health.db.latency_ms}ms
          </span>
        )}
      </button>

      {expanded && (
        <div className="mt-2 pt-2 border-t border-neutral-100 dark:border-white/5 space-y-1 text-[11px] text-neutral-500">
          <div className="flex justify-between">
            <span>UI</span>
            <span className="font-mono text-neutral-700 dark:text-neutral-200">{frontendHash}</span>
          </div>
          {backendHash && (
            <div className="flex justify-between">
              <span>API</span>
              <span className="font-mono text-neutral-700 dark:text-neutral-200">{backendHash}</span>
            </div>
          )}
          <div className="flex justify-between">
            <span>DB</span>
            <span className={`font-mono ${ok ? 'text-emerald-600' : 'text-amber-600'}`}>
              {health?.db?.status || '—'}
              {health?.db?.latency_ms != null && ` (${health.db.latency_ms}ms)`}
            </span>
          </div>
          <div className="flex justify-between">
            <span>Status</span>
            <span className={`font-mono ${ok ? 'text-emerald-600' : 'text-amber-600'}`}>
              {health.status}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
